/**
 * Daily Engagement fast clock (staging only): for one player, 1 IST day lasts
 * N real seconds, so testers walk the 28-day cycle in minutes. Mirrors
 * wcc4-backend:
 * - daily-engagement/daily-engagement.clock.ts (limits, virtual time)
 * - admin/dto/admin-daily-engagement-fast-clock.dto.ts (request, response)
 */

export const FAST_CLOCK_MIN_SECONDS_PER_DAY = 5;
export const FAST_CLOCK_MAX_SECONDS_PER_DAY = 86_400;
export const FAST_CLOCK_DEFAULT_SECONDS_PER_DAY = 10;

const DAY_MS = 86_400_000;
const IST_OFFSET_MS = 5.5 * 3_600_000;

/**
 * Server environments that have a fast clock (backend fastClockAllowed:
 * staging and test; development only when set explicitly, which the 404
 * fallback covers). Production never does.
 */
export const fastClockEnvironment = (nodeEnv: string | undefined): boolean =>
  nodeEnv === 'staging' || nodeEnv === 'test' || nodeEnv === 'development';

const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/** The server takes a UUID (ParseUUIDPipe / IsUUID); anything else is a 400. */
export const isUserId = (value: string): boolean => UUID_RE.test(value.trim());

/** A whole number of seconds the server accepts for secondsPerDay. */
export const isValidSecondsPerDay = (value: unknown): value is number =>
  typeof value === 'number' &&
  Number.isInteger(value) &&
  value >= FAST_CLOCK_MIN_SECONDS_PER_DAY &&
  value <= FAST_CLOCK_MAX_SECONDS_PER_DAY;

/**
 * The player's virtual time `realElapsedMs` after the server reported
 * `virtualNowMs`: it runs 86 400 / secondsPerDay times faster than real time,
 * or at real speed when the fast clock is off (null).
 */
export const virtualTimeAfter = (
  virtualNowMs: number,
  secondsPerDay: number | null,
  realElapsedMs: number
): number =>
  virtualNowMs + (secondsPerDay ? (realElapsedMs * 86_400) / secondsPerDay : realElapsedMs);

/** Real seconds until the player's next IST day starts (rounded up). */
export const realSecondsToNextIstDay = (
  virtualMs: number,
  secondsPerDay: number | null
): number => {
  const msIntoDay = (((virtualMs + IST_OFFSET_MS) % DAY_MS) + DAY_MS) % DAY_MS;
  const virtualMsLeft = DAY_MS - msIntoDay;
  const realMsLeft = secondsPerDay ? (virtualMsLeft * secondsPerDay) / 86_400 : virtualMsLeft;
  return Math.ceil(realMsLeft / 1000);
};

/** `2026-10-18 06:30:00 IST`: the IST wall-clock time of a UTC instant. */
export const formatIst = (ms: number): string =>
  `${new Date(ms + IST_OFFSET_MS).toISOString().slice(0, 19).replace('T', ' ')} IST`;

/** Whole IST days from `fromMs` to `toMs` (calendar days, not 24 h spans). */
export const istDaysBetween = (fromMs: number, toMs: number): number =>
  Math.floor((toMs + IST_OFFSET_MS) / DAY_MS) - Math.floor((fromMs + IST_OFFSET_MS) / DAY_MS);

/** `45 s`, `12 min 5 s`, `3 h 2 min`: a short real-time duration. */
export const formatDuration = (totalSeconds: number): string => {
  const s = Math.max(0, Math.ceil(totalSeconds));
  if (s < 60) return `${s} s`;
  if (s < 3600) return `${Math.floor(s / 60)} min${s % 60 ? ` ${s % 60} s` : ''}`;
  const m = Math.floor((s % 3600) / 60);
  return `${Math.floor(s / 3600)} h${m ? ` ${m} min` : ''}`;
};
